import React, { Component } from 'react';
import { MinusIcon, PlusIcon } from '@glu/icons-react';
import { withStyles, withTheme } from '@glu/theming';
import kebabCase from 'lodash/kebabCase';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { JSSClasses } from '../../proptypes/jss-prop-types';

/*
 * @TODO CB-20313
 * Accordion
 */

/* istanbul ignore next */
const styles = ({
  palette: {
    grey: { G600 },
    primary: { light }
  },
  spacing: { unit },
  transitions: {
    duration: { short, shorter },
    easing: { easeIn, easeInOut }
  },
  typography
}) => ({
  accordion: {
    '& .icon-wrapper svg': {
      fill: '#0074bd'
    },
    '& .collapse': {
      display: 'block',
      height: 'auto',
      transition: `transform ${short}ms ${easeInOut}`,

      '& > *': {
        transition: `opacity ${shorter}ms ${easeIn}`
      }
    },

    '& .collapse-enter, .collapse-exit.collapse-exit-active, .collapse-exit-done': {
      '& > *': {
        opacity: 0
      }
    },

    '& .collapse-enter.collapse-enter-active': {
      '& > *': {
        transitionDelay: shorter,
        opacity: 1
      }
    },

    '& .collapse-exit.collapse-exit-active': {
      transitionDelay: shorter
    }
  },
  expandable: {
    '& .panel-heading-wrapper': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    '& .panel-title': {
      fontWeight: typography.fontWeightRegular,
      fontSize: '16px',
      textTransform: 'capitalize',
      fontFamily: typography.headline2.fontFamily
    },
    '& .panel-subtitle': {
      paddingLeft: `${unit * 3.75}px`,
      fontSize: typography.fontSize,
      color: G600
    },
    '& .panel-heading, .panel-body': {
      padding: 0
    },
    '& .panel-heading': {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      fontSize: '18px',
      color: '#4c4c4c',
      width: '100%'
    },
    '& .icon-wrapper': {
      width: 'auto',
      height: 'auto',
      '&.icon-left': {
        paddingRight: 15
      },
      '&.icon-right': {
        paddingLeft: 15
      }
    },
    '& .panel-body': {
      padding: 0
    },
    '& .heading-top, & .heading-bottom': {
      display: 'flex'
    }
  },
  default: {
    background: light
  },
  optionalField: {
    marginLeft: 'auto'
  }
});

export class AccordionComponent extends Component {
  static propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    chevron: PropTypes.bool,
    classes: JSSClasses,
    className: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    customOptionalFieldCls: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string
    ]),
    panelTitleOptional: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    panelTitle: PropTypes.oneOfType([PropTypes.func, PropTypes.element, PropTypes.string]),
    panelSubTitle: PropTypes.string,
    id: PropTypes.string,
    iconRight: PropTypes.bool,
    mountOnEnter: PropTypes.bool,
    open: PropTypes.bool,
    onToggle: PropTypes.func,
    theme: PropTypes.shape({}),
    unmountOnExit: PropTypes.bool,
    variant: PropTypes.oneOf(['default', 'secondary', 'expandable'])
  };

  static defaultProps = {
    children: [],
    chevron: false,
    classes: {},
    className: 'panel panel-primary',
    panelTitleOptional: '',
    panelTitle: '',
    panelSubTitle: '',
    id: '',
    iconRight: false,
    mountOnEnter: true,
    open: false,
    onToggle: null,
    theme: {},
    unmountOnExit: true,
    variant: 'default'
  };

  state = {
    open: this.props.open
  };

  componentDidMount() {
    this.checkInitialState();
  }

  toggle = (event) => {
    const { onToggle } = this.props;

    if (onToggle) {
      return onToggle(event);
    }

    return this.setState((state) => ({ open: !state.open }));
  };

  /** @description CSSTransition doesn't apply enter-done initialy, only when the state changes */
  checkInitialState() {
    this.setState(({ open }) => ({
      open: open || this.props.open
    }));
  }

  render() {
    const {
      chevron,
      children,
      className,
      classes,
      customOptionalFieldCls,
      iconRight,
      id,
      mountOnEnter,
      onToggle,
      panelSubTitle,
      panelTitle,
      panelTitleOptional,
      theme: {
        transitions: {
          duration: { shorter }
        }
      },
      unmountOnExit,
      variant
    } = this.props;

    const { accordion, optionalField } = classes;
    const { open: stateDriven } = this.state;
    const { open: propDriven } = this.props;
    const open = onToggle ? propDriven : stateDriven;
    const isFn = typeof panelTitle === 'function';

    return (
      <section className={classnames(className, accordion, classes[variant])}>
        <div className="panel-heading-wrapper">
          <div
            aria-controls={`collapse-${id}`}
            aria-expanded={open}
            className="panel-heading"
            id={id}
            onClick={this.toggle}
            onKeyUp={this.toggle}
            role="tab"
            tabIndex="0"
          >
            {isFn && panelTitle({ open, toggle: this.toggle })}
            {!isFn && (
              <>
                <div className="heading-top">
                  {!iconRight && (
                    <div className="icon-wrapper icon-left">
                      {chevron && (open ? <MinusIcon /> : <PlusIcon />)}
                    </div>
                  )}
                  <div className="panel-title" data-qa={`panel-title-${kebabCase(panelTitle)}`}>
                    {panelTitle}
                  </div>
                  {iconRight && (
                    <div className="icon-wrapper icon-right">
                      {chevron && (open ? <MinusIcon /> : <PlusIcon />)}
                    </div>
                  )}
                  <div className={classnames(optionalField, customOptionalFieldCls)}>
                    {panelTitleOptional}
                  </div>
                </div>
                <div className="heading-bottom">
                  <div
                    className="panel-subtitle"
                    data-qa={`panel-subtitle-${kebabCase(panelTitle)}`}
                  >
                    {panelSubTitle}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <CSSTransition
          classNames="collapse"
          in={open}
          mountOnEnter={mountOnEnter}
          timeout={shorter}
          unmountOnExit={unmountOnExit}
        >
          <article
            aria-expanded="true"
            aria-labelledby={id}
            className="panel-collapse collapse collapse-enter collapse-enter-active"
            id={`collapse-${id}`}
            role="tabpanel"
          >
            <div className="panel-body">{children}</div>
          </article>
        </CSSTransition>
      </section>
    );
  }
}

const AccordionStyled = withStyles(styles)(AccordionComponent);

export default withTheme(AccordionStyled);
